import { Clipboard } from '@angular/cdk/clipboard';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import {
  IContact,
  IContactDetails,
  IPractice,
  IStaffer,
  ReferralParticipant,
  isStaffer,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef } from '@principle-theorem/shared';

interface IParticipantDisplayDetails {
  name: string;
  jobTitle?: string;
  practice?: string;
  phone?: string;
  mobileNumber?: string;
  email?: string;
  address?: string;
}

@Component({
  selector: 'pr-referral-participant-details',
  imports: [NgCommonModule, MatIconModule, MatTooltipModule, MatButtonModule],
  templateUrl: './referral-participant-details.component.html',
  styleUrl: './referral-participant-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralParticipantDetailsComponent {
  private _clipboard = inject(Clipboard);
  private _snackbar = inject(MatSnackBar);

  participant = input<ReferralParticipant>();
  contactDetails = input<IContactDetails>();
  practice = input<WithRef<IPractice>>();
  practiceName = input<string>();
  title = input<string>('Participant Details');
  editable = input<boolean>(false);

  isStafferParticipant = computed(() => {
    const participant = this.participant();
    return participant ? isStaffer(participant) : false;
  });

  displayDetails = computed<IParticipantDisplayDetails | undefined>(() => {
    const participant = this.participant();
    const contactDetails = this.contactDetails();

    if (participant) {
      if (isStaffer(participant)) {
        return this._getStafferDetails(participant);
      }
      return this._getContactDetails(participant);
    }

    if (contactDetails) {
      return this._getContactDetailsFromRaw(contactDetails);
    }

    return;
  });

  copyDetailsToClipboard(): void {
    const details = this.displayDetails();
    if (!details) {
      return;
    }

    const lines: string[] = [];

    lines.push(`Name: ${details.name}`);

    if (details.jobTitle) {
      lines.push(`Job Title: ${details.jobTitle}`);
    }

    if (details.practice) {
      lines.push(`Practice: ${details.practice}`);
    }

    if (details.phone) {
      lines.push(`Phone: ${details.phone}`);
    }

    if (details.mobileNumber) {
      lines.push(`Mobile: ${details.mobileNumber}`);
    }

    if (details.email) {
      lines.push(`Email: ${details.email}`);
    }

    if (details.address) {
      lines.push(`Address: ${details.address}`);
    }

    const text = lines.join('\n');
    const success = this._clipboard.copy(text);

    if (success) {
      this._snackbar.open('Details copied to clipboard', undefined, {
        duration: 3000,
      });
    } else {
      this._snackbar.open('Failed to copy to clipboard', undefined, {
        duration: 3000,
      });
    }
  }

  private _getStafferDetails(
    staffer: WithRef<IStaffer>
  ): IParticipantDisplayDetails {
    const practiceData = this.practice();
    return {
      name: staffer.user.name,
      practice: practiceData?.name,
      phone: practiceData?.phone,
      email: practiceData?.email,
      address: practiceData?.address,
    };
  }

  private _getContactDetails(
    contact: WithRef<IContact>
  ): IParticipantDisplayDetails {
    return {
      name: contact.name,
      jobTitle: contact.jobTitle,
      phone: contact.phone,
      mobileNumber: contact.mobileNumber,
      email: contact.email,
      address: contact.address,
    };
  }

  private _getContactDetailsFromRaw(
    details: IContactDetails
  ): IParticipantDisplayDetails {
    return {
      name: details.name,
      jobTitle: details.jobTitle,
      practice: this.practiceName(),
      phone: details.phone,
      mobileNumber: details.mobileNumber,
      email: details.email,
      address: details.address,
    };
  }
}
